import { useCallback, useMemo, useState } from "react";
import type {
	ColumnConfig,
	DefaultRowConfig,
	QuoteColumnType,
} from "../column-types";
import type { QuoteTemplate } from "../../../api/quote-templates-api";
import {
	BLANK_ROWS,
	COLUMN_TYPE_OPTIONS,
	MAX_COLUMNS,
} from "../column-types";
// Preset map and default columns are quote-table-specific; they live in the
// QuoteTable feature so the SpreadsheetEditor remains conceptually generic.
// We import them here to preserve the hook's existing preset-resolution
// semantics. Other callers (e.g. ScheduleOfRates) pass an empty templates
// array and a preset key that isn't in PRESETS — they fall through to the
// BLANK_ROWS branch and never trigger this dependency at runtime.
import {
	DEFAULT_COLUMNS,
	PRESETS,
} from "../../QuoteTable/quote-table-constants";
import { safeRandomUUID } from "newui/utils/general";

// ---------------------------------------------------------------------------
// Public interface
// ---------------------------------------------------------------------------

export type RemoveColumnResult = "removed" | "needs-confirmation" | "blocked";

export interface ApplyPresetResult {
	description?: string;
	name?: string;
	rows: DefaultRowConfig[];
}

export interface UseColumnManagerReturn {
	columns: ColumnConfig[];
	hasUserModified: boolean;
	pendingDeleteId: string | null;
	typeCounts: Partial<Record<QuoteColumnType, number>>;

	addColumn: () => string;
	applyPreset: (
		presetKey: string,
		templates: QuoteTemplate[]
	) => ApplyPresetResult;
	duplicateColumn: (localId: string) => string;
	executeColumnDelete: (localId: string) => void;
	getDisabledReason: (type: QuoteColumnType) => string;
	insertColumn: (localId: string, position: "left" | "right") => string;
	isTypeDisabled: (
		type: QuoteColumnType,
		currentColType?: QuoteColumnType
	) => boolean;
	moveColumn: (localId: string, direction: "left" | "right") => void;
	removeColumn: (localId: string) => RemoveColumnResult;
	reorderColumns: (sourceIndex: number, destIndex: number) => void;
	resetForCreate: () => void;
	resetForEdit: (existingColumns?: ColumnConfig[]) => void;
	updateColumnLabel: (localId: string, label: string) => void;
	updateColumnType: (localId: string, type: QuoteColumnType) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const generateLocalId = (): string => {
	return `col-${safeRandomUUID()}`;
};

const addLocalIds = (cols: Omit<ColumnConfig, "localId">[]): ColumnConfig[] =>
	cols.map((c) => ({ ...c, localId: generateLocalId() }));

const recalcSortOrders = (cols: ColumnConfig[]): ColumnConfig[] =>
	cols.map((c, i) => ({ ...c, sortOrder: i + 1 }));

const buildDefaultColumns = (): ColumnConfig[] => addLocalIds(DEFAULT_COLUMNS);

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useColumnManager = (
	initialColumns?: ColumnConfig[]
): UseColumnManagerReturn => {
	const [columns, setColumns] = useState<ColumnConfig[]>(() => {
		if (initialColumns && initialColumns.length > 0) {
			return initialColumns.map((c) => ({
				...c,
				localId: c.localId || generateLocalId(),
			}));
		}
		return buildDefaultColumns();
	});
	const [hasUserModified, setHasUserModified] = useState(false);
	const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

	// -----------------------------------------------------------------------
	// Derived state
	// -----------------------------------------------------------------------

	const typeCounts = useMemo(() => {
		const counts: Partial<Record<QuoteColumnType, number>> = {};
		for (const col of columns) {
			counts[col.columnType] = (counts[col.columnType] || 0) + 1;
		}
		return counts;
	}, [columns]);

	// "Numeric response" = response_value OR saving (both feed the Total column).
	const hasValue =
		(typeCounts.response_value || 0) + (typeCounts.saving || 0) > 0;

	// -----------------------------------------------------------------------
	// Constraint helpers
	// -----------------------------------------------------------------------

	const isTypeDisabled = useCallback(
		(type: QuoteColumnType, currentColType?: QuoteColumnType): boolean => {
			const option = COLUMN_TYPE_OPTIONS.find((o) => o.value === type);
			if (!option) return false;

			if (type === "total" && !hasValue && currentColType !== "total") {
				return true;
			}
			if (option.maxPerTable === null) return false;
			if (currentColType === type) return false;

			const count = typeCounts[type] || 0;
			return count >= option.maxPerTable;
		},
		[typeCounts, hasValue]
	);

	const getDisabledReason = useCallback(
		(type: QuoteColumnType): string => {
			if (type === "total" && !hasValue) {
				return "Total requires at least one Response: Value or Response: Saving column";
			}
			if (type === "unit_of_measurement") {
				return "Only 1 Units column allowed per table";
			}
			if (type === "response_quantity") {
				return "Only 1 Quantity column allowed per table";
			}
			if (type === "total") {
				return "Only 1 Total column allowed per table";
			}
			return "";
		},
		[hasValue]
	);

	// -----------------------------------------------------------------------
	// Mutation marker
	// -----------------------------------------------------------------------

	const markModified = useCallback(() => {
		setHasUserModified(true);
	}, []);

	// -----------------------------------------------------------------------
	// Column mutations
	// -----------------------------------------------------------------------

	const addColumn = useCallback((): string => {
		if (columns.length >= MAX_COLUMNS) return "";
		const localId = generateLocalId();
		const newCol: ColumnConfig = {
			columnType: "response_text",
			label: "New Column",
			sortOrder: columns.length + 1,
			localId,
		};
		setColumns([...columns, newCol]);
		markModified();
		return localId;
	}, [columns, markModified]);

	const insertColumn = useCallback(
		(targetLocalId: string, position: "left" | "right"): string => {
			if (columns.length >= MAX_COLUMNS) return "";
			const idx = columns.findIndex((c) => c.localId === targetLocalId);
			if (idx === -1) return "";

			const localId = generateLocalId();
			const newCol: ColumnConfig = {
				columnType: "response_text",
				label: "New Column",
				sortOrder: 0, // recalculated below
				localId,
			};
			const insertAt = position === "left" ? idx : idx + 1;
			const next = [...columns];
			next.splice(insertAt, 0, newCol);
			setColumns(recalcSortOrders(next));
			markModified();
			return localId;
		},
		[columns, markModified]
	);

	const duplicateColumn = useCallback(
		(targetLocalId: string): string => {
			if (columns.length >= MAX_COLUMNS) return "";
			const idx = columns.findIndex((c) => c.localId === targetLocalId);
			if (idx === -1) return "";
			const source = columns[idx];

			// Respect per-type caps (e.g. unit_of_measurement / response_quantity /
			// total are max 1). If duplicating would exceed the cap, block.
			const option = COLUMN_TYPE_OPTIONS.find(
				(o) => o.value === source.columnType
			);
			if (option && option.maxPerTable !== null) {
				const count = typeCounts[source.columnType] || 0;
				if (count >= option.maxPerTable) return "";
			}

			const localId = generateLocalId();
			const newCol: ColumnConfig = {
				columnType: source.columnType,
				label: `${source.label} (copy)`,
				sortOrder: 0, // recalculated below
				localId,
			};
			const next = [...columns];
			next.splice(idx + 1, 0, newCol);
			setColumns(recalcSortOrders(next));
			markModified();
			return localId;
		},
		[columns, typeCounts, markModified]
	);

	const wouldRemoveTotalColumn = useCallback(
		(localId: string): boolean => {
			const col = columns.find((c) => c.localId === localId);
			if (!col) return false;
			const hasTotal = columns.some((c) => c.columnType === "total");
			if (!hasTotal) return false;
			// Total requires at least one numeric response column to keep working.
			if (col.columnType !== "response_value" && col.columnType !== "saving") {
				return false;
			}
			const numericResponseCount = columns.filter(
				(c) => c.columnType === "response_value" || c.columnType === "saving"
			).length;
			return numericResponseCount <= 1;
		},
		[columns]
	);

	const removeColumn = useCallback(
		(localId: string): RemoveColumnResult => {
			if (columns.length <= 1) return "blocked";
			if (!columns.some((c) => c.localId === localId)) return "blocked";

			if (wouldRemoveTotalColumn(localId)) {
				setPendingDeleteId(localId);
				return "needs-confirmation";
			}

			const col = columns.find((c) => c.localId === localId);
			let next = columns.filter((c) => c.localId !== localId);
			// Defence in depth: cascade total removal if last response_value was removed via a code path
			// other than wouldRemoveTotalColumn (e.g., concurrent state).
			if (col && col.columnType === "response_value") {
				const remainingValue = next.some(
					(c) => c.columnType === "response_value"
				);
				if (!remainingValue) {
					next = next.filter((c) => c.columnType !== "total");
				}
			}
			setColumns(recalcSortOrders(next));
			markModified();
			return "removed";
		},
		[columns, markModified, wouldRemoveTotalColumn]
	);

	const executeColumnDelete = useCallback(
		(localId: string) => {
			const col = columns.find((c) => c.localId === localId);
			if (!col) {
				setPendingDeleteId(null);
				return;
			}
			let next = columns.filter((c) => c.localId !== localId);
			if (col.columnType === "response_value") {
				const remainingValue = next.some(
					(c) => c.columnType === "response_value"
				);
				if (!remainingValue) {
					next = next.filter((c) => c.columnType !== "total");
				}
			}
			setColumns(recalcSortOrders(next));
			setPendingDeleteId(null);
			markModified();
		},
		[columns, markModified]
	);

	const updateColumnLabel = useCallback(
		(localId: string, label: string) => {
			setColumns((prev) =>
				prev.map((c) => (c.localId === localId ? { ...c, label } : c))
			);
			markModified();
		},
		[markModified]
	);

	const updateColumnType = useCallback(
		(localId: string, type: QuoteColumnType) => {
			setColumns((prev) =>
				prev.map((c) =>
					c.localId === localId ? { ...c, columnType: type } : c
				)
			);
			markModified();
		},
		[markModified]
	);

	const moveColumn = useCallback(
		(localId: string, direction: "left" | "right") => {
			const idx = columns.findIndex((c) => c.localId === localId);
			if (idx === -1) return;
			const newIdx = direction === "left" ? idx - 1 : idx + 1;
			if (newIdx < 0 || newIdx >= columns.length) return;
			const next = [...columns];
			const [moved] = next.splice(idx, 1);
			next.splice(newIdx, 0, moved);
			setColumns(recalcSortOrders(next));
			markModified();
		},
		[columns, markModified]
	);

	const reorderColumns = useCallback(
		(sourceIndex: number, destIndex: number) => {
			if (sourceIndex === destIndex) return;
			if (sourceIndex < 0 || sourceIndex >= columns.length) return;
			if (destIndex < 0 || destIndex >= columns.length) return;
			const next = [...columns];
			const [moved] = next.splice(sourceIndex, 1);
			next.splice(destIndex, 0, moved);
			setColumns(recalcSortOrders(next));
			markModified();
		},
		[columns, markModified]
	);

	const applyPreset = useCallback(
		(presetKey: string, templates: QuoteTemplate[]): ApplyPresetResult => {
			if (presetKey === "") {
				setColumns(buildDefaultColumns());
				setHasUserModified(false);
				return { rows: BLANK_ROWS };
			}

			if (presetKey.startsWith("template_")) {
				const templateId = Number(presetKey.replace("template_", ""));
				const template = templates.find((t) => t.id === templateId);
				if (template) {
					const templateCols: Omit<ColumnConfig, "localId">[] =
						template.columns.map((c) => ({
							columnType: c.column_type as QuoteColumnType,
							label: c.label,
							sortOrder: c.sort_order,
						}));
					const builtCols = addLocalIds(
						templateCols.length > 0 ? templateCols : DEFAULT_COLUMNS
					);
					setColumns(builtCols);
					setHasUserModified(false);
					// Build a sort_order → localId map so we can re-key the template's
					// cells (which use sort_order) onto stable localIds. Without this
					// the cell values break the moment a column is added / removed /
					// reordered because sortOrders get renumbered.
					const sortOrderToLocalId = new Map<number, string>(
						builtCols.map((c) => [c.sortOrder, c.localId])
					);
					const rows: DefaultRowConfig[] =
						template.rows.length > 0
							? template.rows.map((r) => {
									// Cells from the API are keyed by sort_order; remap to
									// the stable localId so add/remove/reorder of columns
									// in the active editor doesn't shift values onto the
									// wrong column. (Any legacy `r.name` has already been
									// folded into `r.cells[primaryDescSortOrder]` by
									// `normalizeTemplate` in quote-templates-api.ts.)
									const remappedCells: Record<string, string> = {};
									for (const [k, v] of Object.entries(r.cells)) {
										const localId = sortOrderToLocalId.get(Number(k));
										if (localId !== undefined) remappedCells[localId] = v;
									}
									return {
										unit_id: r.unit_id,
										sort_order: r.sort_order,
										cells: remappedCells,
									};
								})
							: BLANK_ROWS;
					return {
						rows,
						name: template.quote_table_name ?? template.name,
						description: template.description ?? undefined,
					};
				}
			}

			const preset = PRESETS[presetKey];
			if (preset) {
				const builtCols = addLocalIds(preset.columns);
				setColumns(builtCols);
				setHasUserModified(false);
				// Preset row literals key cells by column sort_order (since presets
				// don't know runtime localIds). Remap to localId so subsequent
				// add/remove/reorder of columns doesn't shift values.
				const sortOrderToLocalId = new Map<number, string>(
					builtCols.map((c) => [c.sortOrder, c.localId])
				);
				const remappedRows: DefaultRowConfig[] =
					preset.rows.length > 0
						? preset.rows.map((r) => {
								const remappedCells: Record<string, string> = {};
								for (const [k, v] of Object.entries(r.cells ?? {})) {
									const localId = sortOrderToLocalId.get(Number(k));
									if (localId !== undefined) remappedCells[localId] = v;
								}
								return {
									unit_id: r.unit_id,
									sort_order: r.sort_order,
									cells: remappedCells,
								};
							})
						: BLANK_ROWS;
				return {
					rows: remappedRows,
					name: preset.name,
					description: preset.description,
				};
			}

			return { rows: BLANK_ROWS };
		},
		[]
	);

	// -----------------------------------------------------------------------
	// Reset
	// -----------------------------------------------------------------------

	const resetForEdit = useCallback((existingColumns?: ColumnConfig[]) => {
		if (existingColumns && existingColumns.length > 0) {
			setColumns(
				existingColumns.map((c) => ({
					...c,
					localId: c.localId || generateLocalId(),
				}))
			);
		} else {
			setColumns(buildDefaultColumns());
		}
		setHasUserModified(false);
		setPendingDeleteId(null);
	}, []);

	const resetForCreate = useCallback(() => {
		setColumns(buildDefaultColumns());
		setHasUserModified(false);
		setPendingDeleteId(null);
	}, []);

	return {
		columns,
		typeCounts,
		hasUserModified,
		pendingDeleteId,
		addColumn,
		duplicateColumn,
		removeColumn,
		executeColumnDelete,
		updateColumnLabel,
		updateColumnType,
		moveColumn,
		insertColumn,
		reorderColumns,
		applyPreset,
		isTypeDisabled,
		getDisabledReason,
		resetForEdit,
		resetForCreate,
	};
};
